import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { Assignment } from '../../types/assignmentTypes';

interface TodoListProps {
  isMobile?: boolean;
}

interface AssignmentWithStatus extends Assignment {
    studentStatus: 'not_started' | 'in_progress' | 'submitted' | 'returned';
    storyId?: string;
    hasFeedback?: boolean;
}

const TodoList: React.FC<TodoListProps> = ({ isMobile = false }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();


  const [assignments, setAssignments] = useState<AssignmentWithStatus[]>([]);
  const [loading, setLoading] = useState(true);

  // Helper function - MOVE OUTSIDE useEffect
  const isOverdue = (dueDate: any) => {
    return dueDate.toDate() < new Date();
  };

  useEffect(() => {
    const loadAssignments = async () => {
      if (!currentUser) return;
  
      try {
        // Get user's classes
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const userData = userDoc.data();
        const userClasses = userData?.classes || [];
  
        if (userClasses.length === 0) {
          setLoading(false);
          return;
        }
  
        // Get all active assignments for user's classes
        const assignmentsQuery = query(
          collection(db, 'assignments'),
          where('status', '==', 'active'),
          where('classId', 'in', userClasses.slice(0, 10))
        );
  
        const assignmentsSnapshot = await getDocs(assignmentsQuery);
        const loadedAssignments: AssignmentWithStatus[] = [];
  
        // For each assignment, get the student's status
        for (const assignmentDoc of assignmentsSnapshot.docs) {
          const assignmentData = {
            id: assignmentDoc.id,
            ...assignmentDoc.data()
          } as Assignment;
  
          const statusDocId = `${currentUser.uid}_${assignmentDoc.id}`;
          const statusDoc = await getDoc(doc(db, 'studentAssignments', statusDocId));
  
          let studentStatus: 'not_started' | 'in_progress' | 'submitted' | 'returned' = 'not_started';
          let storyId: string | undefined;
          let hasFeedback: boolean = false;  // ADD THIS
  
          if (statusDoc.exists()) {
            const statusData = statusDoc.data();
            studentStatus = statusData.status || 'not_started';
            storyId = statusData.storyId;
            hasFeedback = statusData.hasFeedback || false;  // ← needs to be declared first
        }
  
        loadedAssignments.push({
            ...assignmentData,
            studentStatus,
            storyId,
            hasFeedback,  // ADD THIS
        });
        }
  
        // Sort by priority
        loadedAssignments.sort((a, b) => {
          const getPriority = (assignment: AssignmentWithStatus) => {
            if (assignment.studentStatus === 'returned') return 0;
            if (isOverdue(assignment.dueDate)) return 1;
            if (assignment.studentStatus === 'not_started') return 2;
            if (assignment.studentStatus === 'in_progress') return 3;
            return 4;
          };
          
          const priorityDiff = getPriority(a) - getPriority(b);
          if (priorityDiff !== 0) return priorityDiff;
          
          return a.dueDate.toMillis() - b.dueDate.toMillis();
        });
        
        setAssignments(loadedAssignments);
    } catch (error: any) {  // ← Change to 'any'
        console.error('Error loading assignments:', error);
        console.error('Error details:', error?.message);
        console.error('Error code:', error?.code);
      } finally {
        setLoading(false);
      }
    };
  
    loadAssignments();
  }, [currentUser, isOverdue]);

  const handleStartAssignment = async (assignment: AssignmentWithStatus) => {
    // Block guests from accessing assignments
    if (!currentUser) {
      alert('Please create an account to access class assignments! 📚');
      navigate('/signup');
      return;
    }
    
    if (assignment.storyId) {
      // Open existing story
      navigate('/regular-book-editor', {
        state: {
          storyId: assignment.storyId,
          classId: assignment.classId,
          assignmentId: assignment.id,
          assignmentTitle: assignment.title
        }
      });
    } else {
      // Create new story with assignment context
      navigate('/regular-book-editor', {
        state: {
          assignmentId: assignment.id,
          assignmentTitle: assignment.title,
          classId: assignment.classId,
          prompt: assignment.prompt
        }
      });
    }
  };

  const getTimeUntilDue = (dueDate: any) => {
    const now = new Date();
    const due = dueDate.toDate();
    const diffMs = due.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) return 'Overdue';
    if (diffHours < 24) return `Due in ${diffHours}h`;
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const getStatusBadge = (status: string, hasFeedback?: boolean) => {
    switch (status) {
      case 'returned':
        return { emoji: '🔄', text: 'Needs Revision', color: '#DC2626' };
      case 'in_progress':
        return { emoji: '✏️', text: 'In Progress', color: '#F59E0B' };
        case 'submitted':
            return hasFeedback
                ? { emoji: '💬', text: 'Feedback Available', color: '#6a37d4' }
                : { emoji: '✅', text: 'Submitted', color: '#10B981' };
      default:
        return { emoji: '⚪', text: 'Not Started', color: '#999' };
    }
  };

     // Don't show for guests
     if (!currentUser) {
        return null;
      }

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '20px' : '24px',
        marginBottom: isMobile ? '16px' : '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}>
        <div style={{ textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>
          Loading assignments...
        </div>
      </div>
    );
  }

  if (assignments.length === 0) {
    return null;
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '20px' : '24px',
      marginBottom: isMobile ? '16px' : '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      border: '2px solid #6C5CE7'
    }}>
      <h2 style={{
        fontSize: isMobile ? '1.1rem' : '1.3rem',
        fontWeight: '800',
        margin: '0 0 16px 0',
        color: '#6C5CE7',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        ✅ To-Do List
      </h2>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {assignments.map((assignment) => {
          const overdue = isOverdue(assignment.dueDate);
          const statusBadge = getStatusBadge(assignment.studentStatus, assignment.hasFeedback);
          const isReturned = assignment.studentStatus === 'returned';
          
          return (
            <div
              key={assignment.id}
              onClick={() => handleStartAssignment(assignment)}
              style={{
                background: isReturned ? '#FEE2E2' :
                           overdue ? '#FEF2F2' : 
                           '#F9FAFB',
                padding: '16px',
                borderRadius: '12px',
                border: `2px solid ${isReturned ? '#DC2626' :
                                    overdue ? '#EF4444' : 
                                    '#E5E7EB'}`,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(108, 92, 231, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '8px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '700',
                    color: '#1a1a1a',
                    marginBottom: '4px'
                  }}>
                    {assignment.title}
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#666',
                    marginBottom: '4px'
                  }}>
                    {assignment.className}
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#666'
                  }}>
                    {assignment.description}
                  </div>
                </div>

                <div style={{
                  fontSize: '0.8rem',
                  fontWeight: '700',
                  color: overdue ? '#EF4444' : '#6C5CE7',
                  background: overdue ? '#FEE2E2' : '#F3F0FF',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                  marginLeft: '12px'
                }}>
                  {getTimeUntilDue(assignment.dueDate)}
                </div>
              </div>

              {/* Status Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: statusBadge.color,
                background: `${statusBadge.color}22`,
                padding: '4px 10px',
                borderRadius: '12px',
                marginBottom: '8px'
              }}>
                <span>{statusBadge.emoji}</span>
                <span>{statusBadge.text}</span>
              </div>

              {assignment.minWords && (
                <div style={{
                  fontSize: '0.75rem',
                  color: '#999',
                  marginTop: '8px'
                }}>
                  📝 {assignment.minWords}{assignment.maxWords ? `-${assignment.maxWords}` : '+'} words
                </div>
              )}

              <div style={{
                marginTop: '12px',
                fontSize: '0.85rem',
                color: '#6C5CE7',
                fontWeight: '600'
              }}>
                {assignment.studentStatus === 'not_started' && 'Click to start writing →'}
                {assignment.studentStatus === 'in_progress' && 'Continue writing →'}
                {assignment.studentStatus === 'submitted' && 'View submission →'}
                {assignment.studentStatus === 'returned' && 'Revise and resubmit →'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TodoList;